<template>
  <q-card style="width: 100vw; height: 100%">
    <q-toolbar
      class="bg-primary text-secondary fixed issue-details-toolbar"
      :class="{ 'safe-area-header': needInset.all }"
      style="z-index: 9999"
    >
      <q-btn flat icon="close" @click="closeModal" />
      <q-toolbar-title class="text-center q-pl-md">
        <span class="sw-title-secondary">
          {{ $t('common.issue') }} N°{{ issue.meta.id }}
        </span>
      </q-toolbar-title>
      <q-btn unelevated color="primary" icon="more_vert">
        <q-menu>
          <q-list style="min-width: 230px">
            <q-item clickable v-close-popup v-if="isManageable && isCloseable">
              <q-btn
                class="block"
                dense
                flat
                icon="done"
                color="positive"
                :label="$t('issueDetails.close')"
                @click="closeIssue()"
              />
            </q-item>
            <q-item clickable v-close-popup v-if="showForwardIssueButton">
              <q-btn
                dense
                flat
                :disable="issueBeingTransferred"
                color="primary"
                :label="
                  forwardIssueHasAttribute('message')
                    ? company.forwardIssues.message
                    : $t('issueDetails.forwardIssuesMessage')
                "
                :icon="
                  forwardIssueHasAttribute('icon')
                    ? company.forwardIssues.icon
                    : 'send'
                "
                @click="forwardIssue(issue.meta.id)"
              />
            </q-item>
            <q-item clickable v-close-popup>
              <q-btn
                dense
                flat
                color="primary"
                icon="email"
                :label="$t('issueDetails.sendByEmail.title')"
                @click="onSendIssueByEmail = true"
              />
            </q-item>
          </q-list>
        </q-menu>
      </q-btn>
    </q-toolbar>
    <q-scroll-area
      ref="scrollingArea"
      class="bg-white"
      :style="scrollAreaStyle"
    >
      <div class="column q-px-sm q-pb-xl">
        <!-- DETAIL DU SIGNALEMENT -->
        <q-field :label="$t('issueDetails.createdAt')" stack-label>
          <template v-slot:prepend>
            <q-icon name="today" color="primary" />
          </template>
          <template v-slot:control>
            <div>
              {{ $t('issueDetails.on') }}
              {{ $helpers.stringToDateTime(issue.meta.createdAt) }}
            </div>
          </template>
        </q-field>
        <q-field :label="$t('issueDetails.type')" stack-label>
          <template v-slot:prepend>
            <q-icon name="label" color="primary" />
          </template>
          <template v-slot:control>
            <div>{{ issue.category.name }}</div>
          </template>
        </q-field>
        <q-field :label="$t('common.status')" stack-label>
          <template v-slot:prepend>
            <q-icon name="flag" color="primary" />
          </template>
          <template v-slot:control>
            <div v-if="issue.tracking.status">
              {{ $helpers.statusTranslation(issue.tracking.status).label }}
            </div>
          </template>
        </q-field>
        <q-field
          :label="$t('issueDetails.importance')"
          stack-label
          v-show="'disturbance' in issue && !issue.meta.disturbance === null"
        >
          <template v-slot:prepend>
            <q-icon name="report_problem" color="primary" />
          </template>
          <template v-slot:control>
            <div>
              {{ $helpers.priorityApiIdToFrench(issue.meta.disturbance) }}
            </div>
          </template>
        </q-field>
        <q-field
          :label="$t('issueDetails.residence')"
          stack-label
          v-if="'residence' in issue.location"
        >
          <template v-slot:prepend>
            <q-icon name="location_city" color="primary" />
          </template>
          <template v-slot:control>
            <div>{{ issue.location.residence.name }}</div>
          </template>
        </q-field>
        <q-field
          :label="$t('issueDetails.place')"
          stack-label
          v-if="'place' in issue.location"
        >
          <template v-slot:prepend>
            <q-icon name="edit_location" color="primary" />
          </template>
          <template v-slot:control>
            <div>{{ issue.location.place.name }}</div>
          </template>
        </q-field>
        <q-field
          :label="$t('issueDetails.spot')"
          stack-label
          v-if="'spot' in issue.location"
        >
          <template v-slot:prepend>
            <q-icon name="my_location" color="primary" />
          </template>
          <template v-slot:control>
            <div>{{ issue.location.spot.name }}</div>
          </template>
        </q-field>
        <!-- Reasons -->
        <q-field
          :label="$t('issueDetails.reason')"
          stack-label
          v-if="
            'reason' in issue.category &&
            Object.keys(issue.category.reason).length > 0
          "
        >
          <template v-slot:prepend>
            <q-icon name="apps" color="primary" />
          </template>
          <template v-slot:control>
            <div>
              {{ issue.category.reason.name }}
            </div>
          </template>
        </q-field>
        <q-field
          :label="
            company.externalIssuesUrl
              ? $t('issueDetails.externalIssuesUrl')
              : $t('common.complaintNumber')
          "
          stack-label
          v-if="issueHasCode"
        >
          <template v-slot:prepend>
            <q-icon
              color="primary"
              :name="
                issueIsOngoingTracking
                  ? 'cloud_sync'
                  : company.externalIssuesUrl
                  ? 'east'
                  : 'list_alt'
              "
            />
          </template>
          <template v-slot:control>
            <div v-if="issueIsOngoingTracking">
              {{ $t('sync.ongoingTracking') }}
            </div>
            <a
              v-else-if="company.externalIssuesUrl"
              class="text-primary"
              target="_blank"
              :href="externalIssuesUrl(issue.tracking.code)"
              >Ouvrir</a
            >
            <div v-else>{{ issue.tracking.code }}</div>
          </template>
        </q-field>
        <q-field
          v-if="
            'report' in issue.tracking && 'checklist' in issue.tracking.report
          "
          :label="$t('issueDetails.reportAndCheckpointInfo')"
          stack-label
        >
          <template v-slot:prepend>
            <q-icon name="assignment_late" color="warning" />
          </template>
          <template v-slot:control>
            {{ issue.tracking.report.checklist.name }}
            > {{ issue.tracking.report.checkpoint.name }}
          </template>
        </q-field>

        <q-field :label="$t('issueDetails.author')" stack-label>
          <template v-slot:prepend>
            <q-icon name="person" color="primary" />
          </template>
          <template v-slot:control>
            <div v-if="issue.meta.isPrivate">
              {{ authorFullName }} ({{ $t('createIssue.private') }})
            </div>
            <div v-else>{{ authorFullName }}</div>
          </template>
        </q-field>

        <q-field v-if="issue.meta.isRequestTenant">
          <template v-slot:prepend>
            <q-icon>
              <img src="~assets/account-arrow-right.svg" color="primary" />
            </q-icon>
          </template>
          <template v-slot:control>
            <div>{{ $t('createIssue.requestTenant') }}</div>
          </template>
        </q-field>
        <q-field
          :label="$t('common.details')"
          stack-label
          :borderless="!issueHasImg && !issue.tracking.talks?.length"
        >
          <template v-slot:prepend>
            <q-icon name="list" color="primary" />
          </template>
          <template v-slot:control>
            <div>{{ issue.content.message }}</div>
          </template>
        </q-field>
        <q-field
          :label="$t('login.img')"
          stack-label
          v-if="issueHasImg"
          borderless
        >
          <template v-slot:prepend>
            <q-icon name="camera_alt" color="primary" />
          </template>
          <template v-slot:control>
            <div class="q-mt-sm row full-width">
              <div class="col bg-white text-center" ref="imgContainer">
                <q-carousel
                  class="bg-white sw-c-carousel"
                  v-if="issueHasImgs"
                  animated
                  v-model="slide"
                  arrows
                  :navigation="false"
                  infinite
                  control-color="white"
                  control-type="outline"
                  swipeable
                  style="max-height: 400px; height: auto !important"
                  keep-alive
                >
                  <q-carousel-slide
                    v-for="(imgUrl, index) in issue.content.imgsUrls"
                    :key="index"
                    :name="index"
                    style="display: inline-block; padding: 0 8px 0 0"
                  >
                    <q-img :src="imgUrl" loading="eager" />
                  </q-carousel-slide>
                </q-carousel>
                <img
                  v-else
                  :src="issue.content.imgsUrls[0]"
                  style="max-width: 100%"
                />
              </div>
            </div>
          </template>
        </q-field>
        <!-- LISTE DES CHATS -->
        <q-field
          :label="$t('common.messaging')"
          stack-label
          borderless
          class="chat-messages-wrapper"
          style="overflow: hidden"
        >
          <template v-slot:prepend>
            <q-icon name="forum" color="primary" />
          </template>
          <template v-slot:control>
            <div
              class="full-width q-pr-xl"
              v-for="chat in issue.tracking.talks"
              :key="'chat-' + issue.tracking.talks.indexOf(chat)"
              style="overflow: hidden"
            >
              <q-chat-message
                class="chat-message"
                v-if="!chat.message.startsWith('data:image/')"
                :name="
                  chat.author.id === reporter.id
                    ? $t('issueDetails.me')
                    : chat.author.name
                "
                :avatar="avatarURL(chat.author.id)"
                :text="[chat.message]"
                :stamp="$helpers.stringToDateTime(chat['created_at'])"
                :bg-color="chat.author.id === reporter.id ? 'grey-4' : 'blue-5'"
                :sent="chat.author.id === reporter.id"
              />
              <q-chat-message
                class="chat-message"
                v-else-if="chat.file && chat.message === ''"
                :name="
                  chat.author.id === reporter.id
                    ? $t('issueDetails.me')
                    : chat.author.name
                "
                :avatar="avatarURL(chat.author.id)"
                :stamp="$helpers.stringToDateTime(chat['created_at'])"
                :bg-color="chat.author.id === reporter.id ? 'grey-4' : 'blue-5'"
                :sent="chat.author.id === reporter.id"
              >
                <q-icon size="15px" color="black" name="attach_file" />
                <q-btn
                  flat
                  push
                  no-caps
                  color="black"
                  @click="downloadFile(file)"
                  :label="chat.message"
                >
                </q-btn>
              </q-chat-message>
              <q-chat-message
                v-else
                :name="
                  chat.author.id === reporter.id
                    ? $t('issueDetails.me')
                    : chat.author.name
                "
                :avatar="avatarURL(chat.author.id)"
                :stamp="$helpers.stringToDateTime(chat['created_at'])"
                :bg-color="chat.author.id === reporter.id ? 'grey-4' : 'blue-5'"
                :sent="chat.author.id === reporter.id"
              >
                <img
                  :src="chat.message"
                  @click="openImage(chat.message, chat['created_at'])"
                  style="height: 200px; border-radius: 10px; margin-bottom: 5px"
                />
              </q-chat-message>
            </div>
            <div
              class="full-width q-pr-xl"
              style="overflow: hidden"
              v-for="chat in pendingTalks"
              :key="'chat-' + pendingTalks.indexOf(chat)"
            >
              <q-chat-message
                v-if="!chat.message.startsWith('data:image/') && !chat.file"
                class="chat-message"
                :name="$t('issueDetails.me')"
                :avatar="avatarURL(reporter.id)"
                :text="[chat.message]"
                :stamp="`${$t('common.pending')}...`"
                bg-color="'blue-5'"
                :sent="true"
              />
              <q-chat-message
                v-else
                :name="$t('issueDetails.me')"
                :avatar="avatarURL(reporter.id)"
                :stamp="`${$t('issueDetails.sendingTalk')}...`"
                bg-color="'blue-5'"
                :sent="true"
              >
                <template v-if="chat.file && !chat.message">
                  <q-icon size="15px" color="black" name="attach_file" />
                  <q-btn
                    flat
                    push
                    no-caps
                    color="black"
                    @click="downloadFile(file)"
                    :label="chat.message"
                  ></q-btn>
                </template>
                <img
                  v-else-if="chat.message.startsWith('data:image/')"
                  :src="chat.message"
                  @click="openImage(chat.message, chat['created_at'])"
                  style="height: 200px; border-radius: 10px; margin-bottom: 5px"
                />
              </q-chat-message>
            </div>
            <q-dialog v-model="onDownload">
              <q-card style="min-width: 300px">
                <q-card-section class="row items-center q-pb-none">
                  <div class="text-h6">{{ $t('common.waitPlease') }}</div>
                  <q-space></q-space>
                  <q-btn
                    icon="visibility_off"
                    flat
                    round
                    dense
                    v-close-popup
                  ></q-btn>
                </q-card-section>
                <q-card-section style="text-align: center">
                  <q-linear-progress
                    stripe
                    size="10px"
                    :value="parseFloat(progressValue)"
                    color="warning"
                  ></q-linear-progress>
                  <p class="q-mt-lg">{{ $t('common.downloading') }}...</p>
                </q-card-section>
              </q-card>
            </q-dialog>
            <form class="window-width">
              <div class="row justify-between no-wrap">
                <div style="height: fit-content; margin-top: auto">
                  <q-btn
                    @click="openActionSheet()"
                    color="primary"
                    round
                    size="sm"
                    icon="camera_alt"
                    class="buttonHover q-mr-md"
                    :disabled="message.length !== 0"
                    v-show="isCameraSupported"
                  />
                </div>
                <div style="width: 100%" class="q-pl-sm">
                  <q-input
                    ref="messageInput"
                    v-model="message"
                    autogrow
                    dense
                    @blur.stop="$helpers.scrollToElement($refs.bottom)"
                  />
                </div>
                <div style="height: fit-content; margin-top: auto">
                  <q-btn
                    round
                    class="q-ml-md"
                    dense
                    flat
                    icon="send"
                    color="primary"
                    @click="sendTalk()"
                    :disabled="!message.length || isSendingTalk"
                  />
                </div>
              </div>
            </form>
          </template>
        </q-field>
      </div>
    </q-scroll-area>
  </q-card>
  <q-dialog v-model="showImage" maximized style="background: black">
    <div class="full-height" style="overflow: hidden">
      <div class="full-width row justify-end">
        <q-btn
          class="q-mr-sm"
          round
          unelevated
          flat
          @click="downloadImage(imageShow.file)"
          icon="download"
          color="grey-1"
        />
        <q-btn
          round
          unelevated
          flat
          icon="close"
          v-close-popup
          color="grey-1"
        />
      </div>
      <div class="full-height q-pa-xs row justify-center items-center">
        <img class="img_show" :src="imageShow.file" />
      </div>
    </div>
  </q-dialog>
  <q-dialog v-model="onSendIssueByEmail" maximized>
    <SendIssue
      :issueId="issue.meta.id"
      @closeModal="() => (onSendIssueByEmail = false)"
    />
  </q-dialog>
</template>

<script>
import { Notify } from 'quasar'
import { mapGetters } from 'vuex'
import eventBus from 'src/eventBus'
import { padStart } from 'lodash'
import SendIssue from './SendIssue'
import moment from 'moment'
import { useKeyboard } from 'src/composables/useKeyboard'
const uuidv1 = require('uuid/v1')

export default {
  components: { SendIssue },
  props: {
    issueId: { type: String, required: true },
    currentIssue: { type: Object, required: true },
    shouldScroll: { type: Boolean, default: false }
  },
  inject: ['$DB'],
  data() {
    return {
      onDownload: false,
      onSendIssueByEmail: false,
      progressValue: 0,
      issue: {
        meta: {},
        tracking: {
          status: ''
        },
        category: {},
        location: {},
        content: {}
      },
      message: '',
      isSendingTalk: false,
      img: '',
      isCameraSupported: false,
      slide: 0,
      issueBeingTransferred: false,
      isIssueForwarded: false,
      actionSheetConfig: {
        actions: [
          {
            label: this.$t('createIssue.openGallery'),
            icon: 'add_photo_alternate',
            color: 'primary',
            handler: () => {
              this.openGallery()
            }
          },
          {
            label: this.$t('createIssue.takePicture'),
            icon: 'add_a_photo',
            color: 'primary',
            handler: () => {
              this.shootPhoto()
            }
          }
        ],
        dismissLabel: this.$t('createIssue.dismissLabel')
      },
      imageShow: {
        file: null,
        date: ''
      },
      showImage: false,
      scrollAreaStyle: '',
      pendingTalks: []
    }
  },
  created() {
    eventBus.$on('localIssueChange', this.loadIssue)
    this.keyboard = useKeyboard({
      onWillShow: this.onKeyboardWillShow,
      onDidShow: this.onKeyboardDidShow,
      onWillHide: this.onKeyboardWillHide,
      onDidHide: this.onKeyboardDidHide
    })
    this.keyboard.setupKeyboardListeners()
  },
  async mounted() {
    await this.loadIssue()
    await this.deviceReady()
    if (this.shouldScroll) {
      this.$refs.scrollingArea.setScrollPercentage('vertical', 1, 1000)
    }
    this.message = ''
    const toolbar = document.getElementsByClassName('issue-details-toolbar')
    const toolbarHeight = toolbar.item(0)?.clientHeight
    this.scrollAreaStyle = `height: calc(100vh - ${toolbarHeight}px; top: ${toolbarHeight}px; overflow: auto`
    if (this.issueHasImgs) {
      this.preloadImages()
    }
    await this.loadPendingTalks()
  },
  beforeUnmount() {
    this.$store.dispatch('reporter/clearIssueNotifications', this.issue)
    eventBus.$off('localIssueChange', this.loadIssue)

    if (this.keyboard) {
      this.keyboard.removeKeyboardListeners()
    }
  },
  watch: {
    issueId: function () {
      this.loadIssue()
    }
  },
  computed: {
    ...mapGetters({
      reporter: 'reporter/attributes',
      company: 'company/attributes',
      qrCode: 'reporter/qrCode',
      needInset: 'common/needInset'
    }),
    isManageable() {
      const userIsAuthor =
        Number(this.issue.author?.id) === Number(this.reporter.id)
      return this._computeManageability(
        this.reporter.canCloseIssues,
        userIsAuthor,
        this.issue.meta.isPrivate
      )
    },
    isCloseable() {
      return ['pending', 'ongoing'].includes(this.issue.tracking.status)
    },
    issueHasCode() {
      return Boolean(this.issue.tracking.code)
    },
    issueIsOngoingTracking() {
      return (
        Boolean(this.issue.tracking.code) && this.issue.tracking.code === 'sync'
      )
    },
    issueHasImg() {
      return (
        this.issue.content.imgsUrls &&
        Array.isArray(this.issue.content.imgsUrls) &&
        this.issue.content.imgsUrls.length > 0
      )
    },
    issueHasImgs() {
      return (
        this.issue.content.imgsUrls &&
        Array.isArray(this.issue.content.imgsUrls) &&
        this.issue.content.imgsUrls.length > 1
      )
    },
    showForwardIssueButton() {
      return (
        !this.issueHasCode &&
        this.forwardIssueHasAttribute('url') &&
        !this.isIssueForwarded
      )
    },
    authorFullName() {
      return `${this.issue.author?.firstName || ''} ${
        this.issue.author?.lastName || ''
      }`
    }
  },
  methods: {
    externalIssuesUrl(code) {
      return this.company.externalIssuesUrl.replace('%code%', code)
    },
    forwardIssueHasAttribute(info) {
      return (
        this.company.forwardIssues &&
        info in this.company.forwardIssues &&
        this.company.forwardIssues[info]
      )
    },
    forwardIssue(issueId) {
      this.issueBeingTransferred = true
      this.$SowellProxy
        .forwardIssue(issueId, { data: { type: 'issues' } })
        .then(
          () => {
            this.isIssueForwarded = true
            this.issueBeingTransferred = false
            Notify.create({
              message: this.$t('issueDetails.sendSuccess'),
              color: 'info',
              position: 'top'
            })
          },
          () => {
            this.isIssueForwarded = false
            this.issueBeingTransferred = false
            Notify.create({
              message: this.$t('issueDetails.sendError'),
              color: 'warning',
              position: 'top'
            })
          }
        )
    },
    closeModal() {
      eventBus.$emit('closeIssueModal')
    },
    loadIssue(payload) {
      if (payload) {
        if (Number(payload.id) !== Number(this.issueId)) {
          return Promise.resolve()
        }
      }

      return new Promise((resolve, reject) => {
        var self = this
        self.issue = self.currentIssue
        if (self.currentIssue.fromApi) {
          self.issue = self.currentIssue
          return resolve()
        } else {
          self.$DB
            .localIssues()
            .get(self.issueId)
            .then(async (doc) => {
              self.issue = doc
              await self.loadPendingTalks()
              return resolve()
            })
        }
      })
    },
    async findOrCreateTmpIssue(event) {
      var self = this
      let tmpIssue = undefined

      await self.$DB
        .tmpIssues()
        .find({
          selector: {
            event,
            _id: `${self.issue._id}:${event}`
          }
        })
        .then(function (res) {
          if (res.docs.length) {
            tmpIssue = res.docs[0]
          } else {
            tmpIssue = undefined
          }
        })
        .finally(async () => {
          if (!tmpIssue) {
            tmpIssue = {
              _id: `${self.issue._id}:${event}`,
              event,
              tracking: {
                status: self.issue.tracking.status,
                talks: event === 'talk' ? [] : undefined
              }
            }
            await self.$DB.tmpIssues().put(tmpIssue)
            await self.$DB
              .tmpIssues()
              .get(tmpIssue._id)
              .then(function (doc) {
                tmpIssue = doc
              })
          }
        })
      return tmpIssue
    },
    closeIssue() {
      var self = this

      if (self.issue.fromApi) {
        console.log('closing issue from api')
        self.$SowellProxy
          .update('issue', {
            id: self.issue.meta.id,
            status: 'done'
          })
          .then((res) => {
            self.issue.status = 'done'
            self.issue.tracking.status = 'done'
            self.issue['updated-at'] = res.data['updated-at']
            self.closeModal()
          })
          .catch((error) => {
            console.log('Issue status was not updated', error)
            if (error.length && error[0].title) {
              if (error[0]['title'] === 'Access forbiden') {
                Notify.create({
                  message: this.$t('issueDetails.closePermissionError'),
                  color: 'warning',
                  position: 'top'
                })
              } else if (error[0]['title'].startsWith('duplicate detected')) {
                Notify.create({
                  message: this.$t('issueDetails.duplicateError'),
                  color: 'warning',
                  position: 'top'
                })
              } else {
                Notify.create({
                  message: this.$t('issueDetails.closeError'),
                  color: 'warning',
                  position: 'top'
                })
              }
            } else {
              Notify.create({
                message: this.$t('issueDetails.closeError'),
                color: 'warning',
                position: 'top'
              })
            }
          })
      } else {
        console.log('closing issue in local')
        self.findOrCreateTmpIssue('close').then((tmpIssue) => {
          tmpIssue.tracking.status = 'done'
          tmpIssue.scheduledAt = moment().format('DD/MM/YYYY HH:mm')
          self.$DB
            .tmpIssues()
            .put(tmpIssue)
            .then(() => {
              eventBus.$emit('processQueue')
              this.$store.dispatch('audits/createNotification', true)
              self.closeModal()
            })
        })
      }
    },
    async sendTalk() {
      var self = this
      if (self.isSendingTalk) return

      if (self.message.replace(/\s/g, '').length || self.img.length) {
        self.isSendingTalk = true
        var talk = {
          id: uuidv1(),
          author: {
            id: self.reporter.id,
            img: self.reporter.img,
            name: self.reporter.firstName + ' ' + self.reporter.lastName
          },
          created_at: new Date().toISOString(),
          message: self.message || self.img
        }

        await new Promise((resolve) => {
          if (self.issue.fromApi) {
            self.$SowellProxy
              .createTalk(self.issue.meta.id, talk.message)
              .then(() => {
                self.issue.tracking.talks.push(talk)
                self.$DB
                  .localIssues()
                  .put(self.issue)
                  .catch(function (err) {
                    console.log('err saving API result into localIssues', err) // TODO: notify user
                  })
              })
              .catch(() => {
                console.log('cannot send talk to API') // TODO: notify user
              })
              .finally(resolve)
          } else {
            self.findOrCreateTmpIssue('talk').then((tmpIssue) => {
              console.log('tmpIssue to process', tmpIssue)
              tmpIssue.tracking.talks.push(talk)
              tmpIssue.scheduledAt = moment().format('YYYY/MM/DD HH:mm')
              self.$DB
                .tmpIssues()
                .put(tmpIssue)
                .then(() => {
                  self.$root.$emit('processQueue')
                  this.$store.dispatch('audits/createNotification', true)
                })
                .catch((err) => {
                  console.log('update tmpIssue error', {
                    err,
                    tmpIssue
                  })
                })
                .finally(resolve)
            })
          }
        })

        await this.loadPendingTalks()

        this.isSendingTalk = false
        this.message = ''
      }
    },
    avatarURL(authorID) {
      return (
        'https://res.cloudinary.com/sowell/image/upload/v1552407985/ua/' +
        authorID +
        '.png'
      )
    },
    async deviceReady() {
      if (this.$q.platform.is.capacitor || this.qrCode) {
        const { Camera } = await import('Capacitor/@capacitor/camera')
        this.isCameraSupported = true
        this.camera = Camera
      }
    },
    openActionSheet() {
      this.$q
        .bottomSheet({
          class: 'q-pb-xl',
          actions: [
            {
              label: this.$t('createIssue.openGallery'),
              icon: 'add_photo_alternate',
              color: 'primary',
              id: 'photoGalery'
            },
            {
              label: this.$t('createIssue.takePicture'),
              icon: 'add_a_photo',
              color: 'primary',
              id: 'takePhoto'
            }
          ]
        })
        .onOk((action) => {
          this.handleActionSheetEvent(action.id)
        })
        .onCancel(() => {
          // console.log('Dismissed')
        })
        .onDismiss(() => {
          // console.log('I am triggered on both OK and Cancel')
        })
    },
    handleActionSheetEvent(actionId) {
      switch (actionId) {
        case 'photoGalery':
          this.openGallery()
          break
        case 'takePhoto':
          this.shootPhoto()
          break
      }
    },
    async shootPhoto() {
      if (this.$q.platform.is.capacitor || this.qrCode) {
        const { CameraResultType, CameraSource } = await import(
          'Capacitor/@capacitor/camera'
        )
        const image = await this.camera.getPhoto({
          quality: 60,
          allowEditing: false,
          width: 1280,
          height: 720,
          source: CameraSource.Camera,
          resultType: CameraResultType.Base64,
          promptLabelCancel: this.$t('common.cancel'),
          promptLabelPhoto: this.$t('createIssue.openGallery'),
          promptLabelPicture: this.$t('createIssue.takePicture')
        })
        this.shootSuccess(image.base64String)
      }
    },
    async openGallery() {
      if (this.$q.platform.is.capacitor || this.qrCode) {
        const { CameraResultType, CameraSource } = await import(
          'Capacitor/@capacitor/camera'
        )
        const image = await this.camera.getPhoto({
          quality: 60,
          allowEditing: false,
          width: 1280,
          height: 720,
          source: CameraSource.Photos,
          resultType: CameraResultType.Base64,
          promptLabelCancel: this.$t('common.cancel'),
          promptLabelPhoto: this.$t('createIssue.openGallery'),
          promptLabelPicture: this.$t('createIssue.takePicture')
        })
        this.shootSuccess(image.base64String)
      }
    },
    shootSuccess(imageData) {
      this.img = 'data:image/jpeg;base64,' + imageData
      this.sendTalk()
    },
    _computeManageability(canCloseIssues, isAuthor, isPrivate) {
      if (canCloseIssues) {
        return true
      }

      if (!isAuthor) {
        return false
      }

      return isPrivate
    },
    async downloadImage(file) {
      if (!file) return
      // NOTE: file = data:image/jpeg;base64,/9j/4AAQSkZJR
      const regEx = /[^:/]\w+(?=;|,)/
      const type = file.match(regEx)[0]
      const fileName = this.formatDateAsFilename(type)
      if (this.$q.platform.is.capacitor) {
        const { Filesystem, Directory } = await import(
          'Capacitor/@capacitor/filesystem'
        )
        try {
          await Filesystem.writeFile({
            path: fileName,
            data: file,
            directory: Directory.Documents
          }).then(() => {
            Notify.create({
              message: this.$t('issueDetails.fileDownloadSucced'),
              color: 'positive',
              position: 'top'
            })
          })
        } catch (e) {
          Notify.create({
            message: this.$t('issueDetails.fileDownloadFailed'),
            color: 'negative',
            position: 'top'
          })
          console.error('Unable to write file', e)
        }
      } else {
        let a = document.createElement('a')
        a.href = file
        a.download = fileName
        a.click()
      }
    },
    openImage(file, date) {
      this.imageShow = {
        file: file,
        date: date
      }
      this.showImage = true
    },
    formatDateAsFilename: (type) => {
      const dateObj = new Date()
      const year = padStart(dateObj.getFullYear(), 4, '0')
      const month = padStart(dateObj.getMonth() + 1, 2, '0')
      const date = padStart(dateObj.getDate(), 2, '0')
      const hours = padStart(dateObj.getHours(), 2, '0')
      const minutes = padStart(dateObj.getMinutes(), 2, '0')
      const second = padStart(dateObj.getSeconds(), 2, '0')
      const formatedDate = `${year}${month}${date}_${hours}${minutes}${second}`
      return `SoWell_${formatedDate}.${type}`
    },
    preloadImages() {
      const images = this.issue.content.imgsUrls
      images.forEach((img) => {
        const dom = new Image()
        dom.src = img
      })
    },
    loadPendingTalks() {
      this.$DB
        .tmpIssues()
        .find({
          selector: {
            _id: `${this.issue._id}:talk`,
            event: 'talk'
          }
        })
        .then((res) => {
          if (res.docs.length === 0) {
            this.pendingTalks = []
          } else {
            this.pendingTalks = res.docs[0].tracking.talks || []
          }
        })
    },
    onKeyboardWillShow(info) {
      this.keyboard.keyboardHeight.value = info.keyboardHeight
    },
    onKeyboardDidShow(info) {
      this.keyboard.keyboardHeight.value = info.keyboardHeight
      this.$nextTick(() => {
        this.scrollToMessageInput()
      })
    },
    onKeyboardWillHide() {
      this.keyboard.keyboardHeight.value = 0
    },
    onKeyboardDidHide() {
      this.keyboard.keyboardHeight.value = 0
      this.$nextTick(() => {
        setTimeout(() => {
          this.restoreScrollPosition()
        }, 100)
      })
    },
    scrollToMessageInput() {
      this.keyboard.scrollToElement(
        this.$refs.messageInput,
        this.$refs.scrollingArea,
        {
          margin: 20,
          animationDuration: 200
        }
      )
    },
    restoreScrollPosition() {
      this.keyboard.restoreScrollPosition(this.$refs.scrollingArea, {
        animationDuration: 150
      })
    }
  }
}
</script>
<style lang="stylus">
.notVisible {
  visibility: hidden;
}

.chat-messages-wrapper .q-pr-xl {
  padding-right: 0;
}

.chat-messages-wrapper .q-field__control {
  position: relative;
  display: flex;
  flex-direction: column;
}

.chat-messages-wrapper .q-field__label {
  position: absolute;
  top: 0;
  left: 0;
  padding-left: 2rem;
  height: 56px;
  display: flex;
  align-items: center;
  transform: translateY(0%);
}

.chat-messages-wrapper > .q-field__inner > .q-field__control > .q-field__control-container {
  position: static;
  padding-top: 0;
}

.q-field, .q-scrollarea__content {
  width: 100% !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.img_show {
  overflow: hidden;
  width: 100%;
  height: auto;
  max-height: 60%;
}

@media (min-width: 600px) {
  .img_show {
    width: auto !important
  }
}

.q-carousel__control {
  button {
    background-color: rgba(54,50,50,.64) !important
  }
}

.q-img__image {
  max-height: 100%
  max-width: 100%
  width: unset !important
  height: unset !important
}
</style>
